import { useCallback } from 'react';

import {
  EXPORT_BATCH_SIZE,
  getExportPartCount,
} from '~/shared/utils/exportUtils';
import { useXFetch } from '~/shared/utils/useXFetch';

export interface ExportBatch<T = unknown> {
  part: number;
  totalParts: number;
  data: T[];
}

export function useExportFetch() {
  const api = useXFetch();

  const fetchCount = useCallback(
    async (countUrl: string): Promise<number> => {
      const response = await api<{ count?: number }>(countUrl);
      return response.count ?? 0;
    },
    [api]
  );

  const fetchPage = useCallback(
    async (
      dataUrl: string,
      offset: number,
      limit: number,
      responseKey?: string
    ): Promise<unknown[]> => {
      const url = new URL(dataUrl, window.location.origin);
      url.searchParams.set('limit', String(limit));
      url.searchParams.set('offset', String(offset));

      const response = await api<Record<string, unknown>>(`${url.pathname}${url.search}`);

      const arrayKey =
        responseKey && Array.isArray(response[responseKey])
          ? responseKey
          : Object.keys(response).find((k) => Array.isArray(response[k]));

      if (!arrayKey) return [];

      return (response[arrayKey] as unknown[]) ?? [];
    },
    [api]
  );

  const fetchDataInBatches = useCallback(
    async function* <T = unknown>(
      dataUrl: string,
      countUrl: string,
      responseKey?: string
    ): AsyncGenerator<ExportBatch<T>> {
      const total = await fetchCount(countUrl);
      if (total === 0) return;

      const totalParts = getExportPartCount(total);

      for (let part = 1; part <= totalParts; part++) {
        const offset = (part - 1) * EXPORT_BATCH_SIZE;
        const limit = Math.min(EXPORT_BATCH_SIZE, total - offset);
        const data = (await fetchPage(dataUrl, offset, limit, responseKey)) as T[];
        if (data.length === 0) continue;
        yield { part, totalParts, data };
      }
    },
    [fetchCount, fetchPage]
  );

  return { fetchDataInBatches };
}
